import { useState, useEffect } from "react";
import { Form, Button, Spinner, Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import { useConsentimientoAuth, ConsentimientoAuthProvider } from "./ConsentimientoAuthContext";
import { loginConsentimiento, getDisentimientos, getDisentimiento, generarFichaDisentimiento } from "../api/consentimientoAPI";
import API from "../../axios.jsx";

// Misma lista que el formulario público (ConsentimientoPaiHome.jsx).
const MUNICIPIOS = [
  "Leticia", "Puerto Nariño",
  "El Encanto", "La Chorrera", "La Pedrera", "La Victoria", "Mirití-Paraná",
  "Puerto Alegría", "Puerto Arica", "Puerto Santander", "Tarapacá",
];

// fecha_atencion llega como "YYYY-MM-DD" — NO se pasa por new Date():
// eso la interpreta en UTC y en Colombia (UTC-5) mostraría el día anterior.
const formatearFechaAtencion = (s) => {
  if (!s) return "—";
  const [y, m, d] = String(s).slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};
const formatearEdad = (r) =>
  r.menor_edad != null ? `${r.menor_edad} ${(r.menor_edad_unidad || "Años").toLowerCase()}` : "—";

// El estado de la ficha se muestra según lo que realmente pasó: si el
// representante no dejó correo, no es un "fallo" — el PDF quedó listo,
// simplemente no había a quién enviarlo.
function estadoFicha(r) {
  if (r.ficha_estado === "enviada") return { texto: "Enviada por correo", bg: "#E5F3E3", color: "#2C6B2F" };
  if (r.ficha_estado === "fallida") {
    if (!r.representante_correo) return { texto: "PDF listo · sin correo", bg: "#EEF1F0", color: "#4C6360" };
    return { texto: "Falló el envío", bg: "#FCE7DA", color: "#CC5A26" };
  }
  return { texto: "Generando…", bg: "#FFF4D6", color: "#8A6100" };
}

export default function ConsentimientoAdminHome() {
  return (
    <ConsentimientoAuthProvider>
      <ConsentimientoAdminInterno />
    </ConsentimientoAuthProvider>
  );
}

function ConsentimientoAdminInterno() {
  const { usuario, cargando } = useConsentimientoAuth();

  if (cargando) {
    return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}><Spinner /></div>;
  }
  return usuario ? <PanelAdmin /> : <PantallaLogin />;
}

// ── Login ────────────────────────────────────────────────────────────
function PantallaLogin() {
  const { iniciarSesion } = useConsentimientoAuth();
  const [correo, setCorreo] = useState("");
  const [password, setPassword] = useState("");
  const [cargando, setCargando] = useState(false);

  const entrar = async () => {
    if (!correo.trim() || !password) {
      Swal.fire({ icon: "warning", title: "Completa correo y contraseña", timer: 2000, showConfirmButton: false });
      return;
    }
    setCargando(true);
    try {
      const res = await loginConsentimiento(correo.trim(), password);
      iniciarSesion(res.data.token, res.data.usuario);
    } catch (err) {
      Swal.fire("No se pudo iniciar sesión", err.response?.data?.message || "Revisa tus datos e intenta de nuevo.", "error");
    } finally {
      setCargando(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#FBF9F4", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 18, padding: "34px 28px", maxWidth: 360, width: "100%", border: "1px solid #DDE5E0" }}>
        <p style={{ fontSize: 12.5, fontWeight: 700, color: "#2BA8C4", marginBottom: 4 }}>Secretaría de Salud Departamental</p>
        <h2 style={{ fontSize: 21, fontWeight: 800, color: "#0E4F57", marginBottom: 20 }}>Panel — Disentimientos</h2>

        <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Correo</Form.Label>
        <Form.Control value={correo} onChange={e => setCorreo(e.target.value)} placeholder="correo@institucion.gov.co" className="mb-3" />

        <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Contraseña</Form.Label>
        <Form.Control type="password" value={password} onChange={e => setPassword(e.target.value)} className="mb-4"
          onKeyDown={e => e.key === "Enter" && entrar()} />

        <Button onClick={entrar} disabled={cargando}
          style={{ width: "100%", background: "#0E4F57", border: "none", padding: 11, fontWeight: 700 }}>
          {cargando ? "Ingresando..." : "Ingresar"}
        </Button>
      </div>
    </div>
  );
}

// ── Panel principal ──────────────────────────────────────────────────
function PanelAdmin() {
  const { usuario, cerrarSesion } = useConsentimientoAuth();
  const [registros, setRegistros] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [detalleId, setDetalleId] = useState(null);
  const [generandoId, setGenerandoId] = useState(null);

  const [fMunicipio, setFMunicipio] = useState("");
  const [fDesde, setFDesde] = useState("");
  const [fHasta, setFHasta] = useState("");
  const [fBuscar, setFBuscar] = useState("");

  const cargar = () => {
    setCargando(true);
    getDisentimientos({ municipio: fMunicipio || undefined, desde: fDesde || undefined, hasta: fHasta || undefined })
      .then(res => setRegistros(res.data || []))
      .catch(() => Swal.fire("No se pudo cargar", "Revisa tu conexión e intenta de nuevo.", "error"))
      .finally(() => setCargando(false));
  };

  useEffect(() => { cargar(); }, [fMunicipio, fDesde, fHasta]);

  const q = fBuscar.trim().toLowerCase();
  const filtrados = registros.filter(r =>
    !q
    || (r.menor_nombre || "").toLowerCase().includes(q)
    || (r.representante_nombre || "").toLowerCase().includes(q)
    || (r.menor_documento || "").includes(q)
    || (r.representante_documento || "").includes(q)
  );

  const generarFicha = async (id) => {
    setGenerandoId(id);
    try {
      const res = await generarFichaDisentimiento(id);
      const enviada = res.data?.disentimiento?.ficha_estado === "enviada";
      Swal.fire({
        icon: enviada ? "success" : "info",
        title: res.data?.message || "Ficha generada",
        timer: 2400, showConfirmButton: false,
      });
      cargar();
    } catch (err) {
      Swal.fire("No se pudo generar la ficha", err.response?.data?.message || "Intenta de nuevo.", "error");
    } finally {
      setGenerandoId(null);
    }
  };

  const enviadas = registros.filter(r => r.ficha_estado === "enviada").length;
  const sinCorreo = registros.filter(r => !r.representante_correo).length;

  return (
    <div style={{ minHeight: "100vh", background: "#FBF9F4" }}>
      <div style={{ background: "#0E4F57", color: "#fff", padding: "14px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div>
          <p style={{ fontSize: 12, opacity: .8, margin: 0 }}>Secretaría de Salud Departamental</p>
          <p style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Constancias de disentimiento de vacunación</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 13 }}>{usuario.nombre}</span>
          <Button size="sm" variant="outline-light" onClick={cerrarSesion}>Salir</Button>
        </div>
      </div>

      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "26px 24px 60px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 22 }}>
          <TarjetaStat num={registros.length} label="Total registradas" />
          <TarjetaStat num={enviadas} label="Fichas enviadas por correo" color="#2C6B2F" />
          <TarjetaStat num={sinCorreo} label="Sin correo del representante" color="#4C6360" />
          <TarjetaStat num={new Set(registros.map(r => r.municipio)).size} label="Municipios con registros" />
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", background: "#fff", border: "1px solid #DDE5E0", borderRadius: 14, padding: 14, marginBottom: 18 }}>
          <div>
            <Form.Label style={{ fontSize: 11.5, marginBottom: 4 }}>Municipio</Form.Label>
            <Form.Select size="sm" value={fMunicipio} onChange={e => setFMunicipio(e.target.value)} style={{ minWidth: 180 }}>
              <option value="">Todos</option>
              {MUNICIPIOS.map(m => <option key={m} value={m}>{m}</option>)}
            </Form.Select>
          </div>
          <div>
            <Form.Label style={{ fontSize: 11.5, marginBottom: 4 }}>Atención desde</Form.Label>
            <Form.Control size="sm" type="date" value={fDesde} onChange={e => setFDesde(e.target.value)} />
          </div>
          <div>
            <Form.Label style={{ fontSize: 11.5, marginBottom: 4 }}>Atención hasta</Form.Label>
            <Form.Control size="sm" type="date" value={fHasta} onChange={e => setFHasta(e.target.value)} />
          </div>
          <div style={{ flexGrow: 1, minWidth: 180 }}>
            <Form.Label style={{ fontSize: 11.5, marginBottom: 4 }}>Buscar</Form.Label>
            <Form.Control size="sm" value={fBuscar} onChange={e => setFBuscar(e.target.value)} placeholder="Nombre o documento del niño o del representante..." />
          </div>
          <Button size="sm" variant="link" onClick={() => { setFMunicipio(""); setFDesde(""); setFHasta(""); setFBuscar(""); }}
            style={{ color: "#CC5A26", fontWeight: 700, fontSize: 12.5, textDecoration: "none" }}>
            Limpiar filtros
          </Button>
        </div>

        {cargando ? (
          <div style={{ textAlign: "center", padding: 40 }}><Spinner /></div>
        ) : (
          <div style={{ background: "#fff", border: "1px solid #DDE5E0", borderRadius: 14, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
              <thead>
                <tr style={{ borderBottom: "1.5px solid #DDE5E0" }}>
                  {["Fecha", "Niño, niña o adolescente", "Representante", "Municipio", "Vacuna(s)", "Ficha", "Acciones"].map(h => (
                    <th key={h} style={{ textAlign: "left", fontSize: 11.5, textTransform: "uppercase", color: "#83958F", padding: "12px 14px", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtrados.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: "center", padding: 30, color: "#83958F" }}>Sin registros para estos filtros</td></tr>
                ) : filtrados.map(r => {
                  const est = estadoFicha(r);
                  return (
                    <tr key={r.id} style={{ borderBottom: "1px solid #DDE5E0" }}>
                      <td style={{ padding: "13px 14px", whiteSpace: "nowrap" }}>{formatearFechaAtencion(r.fecha_atencion)}</td>
                      <td style={{ padding: "13px 14px" }}>
                        <div style={{ fontWeight: 600 }}>{r.menor_nombre}</div>
                        <div style={{ fontSize: 12, color: "#83958F" }}>{formatearEdad(r)}</div>
                      </td>
                      <td style={{ padding: "13px 14px" }}>
                        <div style={{ fontWeight: 600 }}>{r.representante_nombre}</div>
                        <div style={{ fontSize: 12, color: "#83958F" }}>{r.representante_vinculo || ""}</div>
                      </td>
                      <td style={{ padding: "13px 14px" }}>{r.municipio}</td>
                      <td style={{ padding: "13px 14px", maxWidth: 220, fontSize: 12.5 }}>
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                          {r.vacunas_ofrecidas || "—"}
                        </div>
                      </td>
                      <td style={{ padding: "13px 14px" }}>
                        <span style={{ fontSize: 11.5, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: est.bg, color: est.color, whiteSpace: "nowrap" }}>
                          {est.texto}
                        </span>
                      </td>
                      <td style={{ padding: "13px 14px" }}>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <Button size="sm" variant="outline-secondary" onClick={() => setDetalleId(r.id)} style={{ fontSize: 12 }}>
                            Ver
                          </Button>
                          <Button size="sm" disabled={generandoId === r.id} onClick={() => generarFicha(r.id)} style={{ background: "#0E4F57", border: "none", fontSize: 12 }}>
                            {generandoId === r.id ? "..." : (r.ficha_url ? "Regenerar" : "Generar ficha")}
                          </Button>
                          {r.ficha_url && (
                            <Button size="sm" variant="outline-secondary" as="a"
                              href={`${API.defaults.baseURL}${r.ficha_url}`} target="_blank" rel="noreferrer"
                              style={{ fontSize: 12 }}>
                              Descargar
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detalleId && <DetalleModal id={detalleId} onClose={() => setDetalleId(null)} />}
    </div>
  );
}

// ── Detalle completo de una constancia (incluye la firma, que el listado no trae) ──
function DetalleModal({ id, onClose }) {
  const [reg, setReg] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    getDisentimiento(id)
      .then(res => setReg(res.data))
      .catch(() => setError(true));
  }, [id]);

  return (
    <Modal show onHide={onClose} centered scrollable size="lg">
      <Modal.Header closeButton style={{ background: "#0E4F57", color: "#fff" }}>
        <Modal.Title style={{ fontSize: "1rem" }}>{reg ? `Constancia ${reg.codigo}` : "Constancia"}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontSize: 13.5 }}>
        {error && <p className="text-danger mb-0">No se pudo cargar el detalle. Revisa tu conexión e intenta de nuevo.</p>}
        {!reg && !error && <div style={{ textAlign: "center", padding: 30 }}><Spinner /></div>}
        {reg && (
          <>
            <Bloque titulo="Datos de atención">
              <Dato label="Municipio" valor={reg.municipio} />
              <Dato label="Fecha" valor={formatearFechaAtencion(reg.fecha_atencion)} />
              <Dato label="Hora" valor={reg.hora_atencion} />
            </Bloque>
            <Bloque titulo="Niño, niña o adolescente">
              <Dato label="Nombre" valor={reg.menor_nombre} />
              <Dato label="Documento" valor={reg.menor_documento} />
              <Dato label="Edad" valor={formatearEdad(reg)} />
            </Bloque>
            <Bloque titulo="Madre, padre o representante legal">
              <Dato label="Nombre" valor={reg.representante_nombre} />
              <Dato label="Documento" valor={reg.representante_documento} />
              <Dato label="Vínculo" valor={reg.representante_vinculo} />
              <Dato label="Teléfono" valor={reg.representante_telefono} />
              <Dato label="Correo" valor={reg.representante_correo} />
            </Bloque>
            <Bloque titulo="Vacunación ofrecida">
              <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>{reg.vacunas_ofrecidas || "—"}</p>
            </Bloque>
            <Bloque titulo="Motivo de la decisión">
              <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>{reg.motivo_decision || "—"}</p>
            </Bloque>
            <Bloque titulo="Constancia y firmas">
              <Dato label="Profesional o vacunador" valor={reg.profesional_nombre} />
              <Dato label="Cargo" valor={reg.profesional_cargo} />
              {reg.firma_base64 ? (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12, color: "#83958F", marginBottom: 4 }}>Firma del representante</div>
                  <img src={reg.firma_base64} alt="Firma" style={{ maxWidth: 280, width: "100%", border: "1px solid #DDE5E0", borderRadius: 10, background: "#fff" }} />
                </div>
              ) : (
                <>
                  <Dato label="Firma" valor="No firmó" />
                  <Dato label="¿Se ofreció lectura y firma?" valor={reg.se_ofrecio_lectura_firma === "Si" ? "Sí" : reg.se_ofrecio_lectura_firma} />
                  <Dato label="Testigo" valor={reg.testigo_nombre} />
                </>
              )}
            </Bloque>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

function Bloque({ titulo, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: ".04em", textTransform: "uppercase", color: "#0E4F57", background: "#E4F5F8", borderRadius: 6, padding: "5px 11px", marginBottom: 8 }}>{titulo}</div>
      {children}
    </div>
  );
}
function Dato({ label, valor }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "5px 0", borderBottom: "1px solid #F1F3F2" }}>
      <span style={{ color: "#83958F", flexShrink: 0 }}>{label}</span>
      <span style={{ fontWeight: 600, textAlign: "right" }}>{valor || "—"}</span>
    </div>
  );
}

function TarjetaStat({ num, label, color }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #DDE5E0", borderRadius: 14, padding: "15px 16px" }}>
      <div style={{ fontSize: 26, fontWeight: 800, color: color || "#0E4F57" }}>{num}</div>
      <div style={{ fontSize: 12, color: "#4C6360", fontWeight: 600 }}>{label}</div>
    </div>
  );
}